'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import Modal from '@/components/Modal';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useNewsletterModal } from '@/providers/NewsletterModalProvider';

import NewsletterRegistrationView from './NewsletterRegistrationView';

/**
 * Newsletter signup in a modal, opened by the bell icons on Specials, Premieres and the Highlights
 * section (CIN-13995).
 *
 * Restored from the pre-CIN-13768 modal, but wrapping the current Figma-styled
 * NewsletterRegistrationView rather than the removed NewsletterSubscribeUI — the modal is only the
 * container, so there is one signup form to maintain, not two.
 *
 * It passes the entry point's topics straight through, so the modal pre-selects only those. The
 * standalone /newsletter page keeps its all-topics-on default.
 */
export default function NewsletterSubscribeModal() {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const { isOpen, closeModal, preselectedTopics } = useNewsletterModal();

    const handleClose = useCallback(() => {
        closeModal();
    }, [closeModal]);

    return (
        isOpen && (
            <Modal
                domainConfiguration={domainConfiguration}
                modalIsOpen={isOpen}
                setModalIsOpen={(open) => {
                    if (!open) handleClose();
                }}
                className="max-w-[660px]"
                onClose={handleClose}>
                <NewsletterRegistrationView
                    topicPreselection={preselectedTopics}
                    onSubscribed={handleClose}
                />
                {/* "Just open the settings page, if someone wants to" — the modal is the quick
                    path, the full topic list stays one click away. */}
                <div className="flex justify-center pb-16">
                    <Link
                        href="/newsletter"
                        onClick={handleClose}
                        className="text-body-12 underline hover:opacity-80 transition-opacity"
                        style={{ color: domainConfiguration.style.primaryColor }}>
                        {t('newsletter-modal.open-settings')}
                    </Link>
                </div>
            </Modal>
        )
    );
}
